import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { useOverdueTasks } from '@/hooks/useProjects'
import { AlertTriangle, Calendar, User, CheckCircle } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { Link } from 'react-router-dom'

export function OverdueTasksWidget() {
  const { data: overdueTasks, isLoading } = useOverdueTasks()

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 flex justify-center">
          <Spinner />
        </div>
      </Card>
    )
  }

  if (!overdueTasks || overdueTasks.length === 0) {
    return (
      <Card>
        <div className="p-6">
          <EmptyState
            icon={<CheckCircle className="w-8 h-8 text-mint-500" />}
            title="Aucune tâche en retard"
            description="Toutes vos tâches sont à jour"
          />
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h3 className="font-semibold text-brown-900">
            Tâches en retard ({overdueTasks.length})
          </h3>
        </div>

        <div className="space-y-3">
          {overdueTasks.slice(0, 5).map(task => {
            const daysOverdue = task.due_date
              ? Math.floor((Date.now() - new Date(task.due_date).getTime()) / 86400000)
              : 0

            return (
              <Link
                key={task.id}
                to={`/projects/${task.project_id}`}
                className="block p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-brown-900 truncate">{task.titre}</p>
                    <p className="text-sm text-brown-600 mt-1 truncate">
                      {task.project?.nom || 'Projet'}
                    </p>
                  </div>
                  <Badge variant="red" size="sm" className="flex-shrink-0 ml-2">
                    {daysOverdue}j
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-brown-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(task.due_date!, { day: 'numeric', month: 'short' })}
                  </span>
                  {task.assignee && (
                    <span className="flex items-center gap-1 truncate">
                      <User className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{task.assignee.full_name}</span>
                    </span>
                  )}
                </div>
              </Link>
            )
          })}
        </div>

        {overdueTasks.length > 5 && (
          <div className="mt-4 text-center">
            <Link
              to="/projects"
              className="text-sm text-brown-600 hover:text-brown-900 font-medium"
            >
              Voir toutes les tâches en retard ({overdueTasks.length})
            </Link>
          </div>
        )}
      </div>
    </Card>
  )
}
